import React from 'react';
import { BOMCalculationResult } from '../../../../../utils';

interface StoredBOMData {
  total_rails: number;
  total_splice_kits: number;
  total_mid_clamps: number;
  total_end_clamps: number;
  total_grounding_lugs: number;
  is_manual_override: boolean;
}

interface BillOfMaterialProps {
  isEnabled: boolean;
  calculatedValues: BOMCalculationResult;
  storedValues: StoredBOMData;
  onUpdate: (data: Partial<StoredBOMData>) => void;
}

interface BOMField {
  calcKey: keyof BOMCalculationResult;
  storeKey: keyof Omit<StoredBOMData, 'is_manual_override'>;
  label: string;
  color: string;
  icon: React.ReactNode;
}

const BOM_FIELDS: BOMField[] = [
  {
    calcKey: 'totalRails', storeKey: 'total_rails', label: 'Total Rails',
    color: 'from-blue-500 to-blue-600',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16" />,
  },
  {
    calcKey: 'totalSpliceKits', storeKey: 'total_splice_kits', label: 'Splice Kits',
    color: 'from-violet-500 to-purple-600',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />,
  },
  {
    calcKey: 'totalMidClamps', storeKey: 'total_mid_clamps', label: 'Mid Clamps',
    color: 'from-emerald-500 to-teal-600',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h8m-4-4v8m-4 4h8" />,
  },
  {
    calcKey: 'totalEndClamps', storeKey: 'total_end_clamps', label: 'End Clamps',
    color: 'from-amber-500 to-orange-600',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4" />,
  },
  {
    calcKey: 'totalGroundingLugs', storeKey: 'total_grounding_lugs', label: 'Grounding Lugs',
    color: 'from-rose-500 to-red-600',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />,
  },
];

export const BillOfMaterial: React.FC<BillOfMaterialProps> = ({
  isEnabled,
  calculatedValues,
  storedValues,
  onUpdate
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const hasOverrides = storedValues.is_manual_override;

  const handleValueChange = (storeKey: keyof Omit<StoredBOMData, 'is_manual_override'>, value: string) => {
    const numValue = parseInt(value) || 0;
    onUpdate({ [storeKey]: numValue, is_manual_override: true });
  };

  const toggleEditMode = () => {
    setIsEditing(!isEditing);
  };

  const getDisplayValue = (calcKey: keyof BOMCalculationResult, storeKey: keyof Omit<StoredBOMData, 'is_manual_override'>): number => {
    if (hasOverrides) {
      return storedValues[storeKey];
    }
    return calculatedValues[calcKey];
  };

  if (!isEnabled) {
    return (
      <div className="text-center py-8">
        <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
          <svg className="w-6 h-6 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <p className="text-sm text-slate-400 font-medium">Complete structure configuration</p>
        <p className="text-xs text-slate-300 mt-0.5">to view BOM calculations</p>
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] 3xl:text-xs text-slate-400 font-medium">
          {isEditing ? 'Manual edit mode — values override calculations' : hasOverrides ? 'Manually edited values' : 'Auto-calculated from structure configuration'}
        </p>
        <div className="flex items-center gap-2">
          {hasOverrides && !isEditing && (
            <button
              onClick={() => onUpdate({
                total_rails: calculatedValues.totalRails,
                total_splice_kits: calculatedValues.totalSpliceKits,
                total_mid_clamps: calculatedValues.totalMidClamps,
                total_end_clamps: calculatedValues.totalEndClamps,
                total_grounding_lugs: calculatedValues.totalGroundingLugs,
                is_manual_override: false
              })}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] 3xl:text-xs font-semibold bg-orange-50 text-orange-500 border border-orange-200 hover:bg-orange-100 hover:text-orange-600 transition-all shadow-sm"
              title="Reset to auto-calculated values"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Reset
            </button>
          )}
          <button
            onClick={toggleEditMode}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] 3xl:text-xs font-semibold transition-all shadow-sm ${
              isEditing
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : 'bg-blue-50 text-blue-500 border border-blue-200 hover:bg-blue-100 hover:text-blue-600'
            }`}
            title={isEditing ? 'Exit edit mode' : 'Edit values manually'}
          >
            {isEditing ? (
              <>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                </svg>
                Done
              </>
            ) : (
              <>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Edit
              </>
            )}
          </button>
        </div>
      </div>

      <hr className="border-slate-200/80 my-3" />

      {isEditing && (
        <div className="mb-3 flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200/80 rounded-lg">
          <svg className="w-4 h-4 text-amber-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xs text-amber-700 font-medium">Manual mode — values will override auto-calculations</p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {BOM_FIELDS.map(({ calcKey, storeKey, label, color, icon }) => {
            const val = getDisplayValue(calcKey, storeKey);
            return (
              <div
                key={storeKey}
                className={`rounded-xl p-3 border text-center transition-all ${
                  isEditing
                    ? 'border-blue-200 bg-blue-50/30 shadow-sm'
                    : 'border-slate-200/60 bg-gradient-to-br from-white to-slate-50/80'
                }`}
              >
                <div className={`w-7 h-7 2xl:w-8 2xl:h-8 3xl:w-9 3xl:h-9 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center mx-auto mb-2 shadow-sm`}>
                  <svg className="w-3.5 h-3.5 2xl:w-4 2xl:h-4 3xl:w-5 3xl:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {icon}
                  </svg>
                </div>
                <label className="block text-[9px] 2xl:text-[10px] 3xl:text-xs uppercase tracking-wider text-slate-400 font-bold mb-1.5">
                  {label}
                </label>
                <input
                  type="number"
                  min="0"
                  value={val}
                  onChange={(e) => handleValueChange(storeKey, e.target.value)}
                  disabled={!isEditing}
                  className={`w-full h-8 2xl:h-9 3xl:h-10 px-2 text-sm 2xl:text-base 3xl:text-lg font-bold rounded-lg border text-center transition-all ${
                    isEditing
                      ? 'bg-white border-blue-300 text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 shadow-sm'
                      : val > 0
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-700 cursor-default'
                        : 'bg-slate-50 border-slate-200 text-slate-400 cursor-default'
                  }`}
                />
              </div>
            );
          })}
        </div>
    </div>
  );
};
